// When to ask for background pictures: photos of the station with none of the project's objects in it, and of the
// things that pass through it (hands, tools, packaging). Pure, so it can be tested; the Train page shows it.
//
// Why: a model trained only on pictures that contain its objects learns "something is always there" and fires on
// the desk, the conveyor or a hand (the SPS badge model called a hand a card, 28 Oct 2026). Pictures a person
// marked Empty train as background images (an empty label file), which is what teaches the model to stay quiet.
// Many users cannot take any (the photos came from a customer, the station is elsewhere): the Augment page's
// "Make background pictures" erases the objects from copies of the project's own pictures (lib/backgroundImages.ts).
// A real photo is still better, and the advice says so: generated ones are the fallback.
import type { AnnotationStats } from "../api/ml";

/** At least one background picture per this many pictures with objects. */
export const BACKGROUND_PER_IMAGES = 10;
/** Never ask for fewer than this many. */
export const BACKGROUND_MIN = 5;

export interface BackgroundAdvice {
  kind: "none" | "few";
  /** How many background pictures the project should have at least. */
  wanted: number;
  /** How many it has. */
  have: number;
  /** How many more it needs. */
  more: number;
  /** One or two sentences: what is short, and what to do. */
  text: string;
}

type Stats = Pick<AnnotationStats, "reviewed_image_count" | "augmented_reviewed_image_count" | "empty" | "generated_background_count">;

/**
 * Original pictures with objects: augmented copies and the background pictures themselves do not count. A generated
 * background picture is counted by the server as a copy and as empty, so it is added back once.
 */
export function picturesWithObjects(s: Stats): number {
  return Math.max(0, s.reviewed_image_count - (s.augmented_reviewed_image_count ?? 0) - (s.empty ?? 0) + (s.generated_background_count ?? 0));
}

/** How many background pictures a project of `withObjects` pictures should have at least. */
export function backgroundWanted(withObjects: number): number {
  return Math.max(BACKGROUND_MIN, Math.ceil(withObjects / BACKGROUND_PER_IMAGES));
}

/**
 * The background-picture suggestion, or null once the project has enough (or has no reviewed pictures yet, when the
 * answer is to annotate). The server's `empty` count is absent from older servers: then nothing is said.
 * `canGenerate`: whether the Augment page's "Make background pictures" is open to this user (Pro), so the advice
 * can offer it as the fallback.
 */
export function backgroundAdvice(s: Stats, canGenerate = false): BackgroundAdvice | null {
  if (s.empty === undefined) return null;
  const withObjects = picturesWithObjects(s);
  if (withObjects <= 0) return null;
  const wanted = backgroundWanted(withObjects);
  const have = s.empty;
  if (have >= wanted) return null;
  const more = wanted - have;
  const take = `Take ${more} more photo${more === 1 ? "" : "s"} of the station with nothing on it if you can, and a few with a hand, a tool or packaging in view; upload them and mark them Empty in the gallery.`
    + (canGenerate ? " Otherwise, make them from your photos: copies with the objects erased. Real photos are still better." : "");
  if (have === 0) {
    return { kind: "none", wanted, have, more, text: `No background pictures. Without photos of the empty station the model learns that something is always there, and fires on the desk or on a hand. ${take}` };
  }
  return { kind: "few", wanted, have, more, text: `Only ${have} background picture${have === 1 ? "" : "s"} for ${withObjects} with objects; about 1 in ${BACKGROUND_PER_IMAGES} should show the station empty. ${take}` };
}
